/**
 * QultureUp schemas
 *
 * Validation schemas for QultureUp hero - cultural adaptation and microlearning.
 */

import { z } from 'zod';

// ═══════════════════════════════════════════════════════════════════════
// WORLD & TRACK SCHEMAS
// ═══════════════════════════════════════════════════════════════════════

/**
 * World progress schema
 */
export const worldProgressSchema = z.object({
  worldId: z.uuid(),
  completedMissions: z.number().int().min(0),
  totalMissions: z.number().int().min(0),
  progressPercentage: z.number().min(0).max(100),
  unlockedAt: z.coerce.date().optional(),
  completedAt: z.coerce.date().optional(),
});

/**
 * Track progress schema
 */
export const trackProgressSchema = z.object({
  trackId: z.uuid(),
  worldId: z.uuid(),
  completedMissions: z.number().int().min(0),
  totalMissions: z.number().int().min(0),
  progressPercentage: z.number().min(0).max(100),
  currentMissionId: z.uuid().optional(),
});

// ═══════════════════════════════════════════════════════════════════════
// MISSION SCHEMAS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Start mission schema
 */
export const startMissionSchema = z.object({
  missionId: z.uuid(),
  journeyInstanceId: z.uuid(),
});

/**
 * Complete mission schema
 */
export const completeMissionSchema = z.object({
  missionId: z.uuid(),
  journeyInstanceId: z.uuid(),
  timeSpentSeconds: z.number().int().min(0),
  score: z.number().min(0).max(100).optional(),
});

/**
 * Mission feedback schema
 */
export const missionFeedbackSchema = z.object({
  missionId: z.uuid(),
  rating: z.number().int().min(1).max(5),
  feedback: z.string().max(1000).optional(),
  helpful: z.boolean().optional(),
});

// ═══════════════════════════════════════════════════════════════════════
// QUIZ SCHEMAS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Quiz answer schema
 */
export const quizAnswerSchema = z.object({
  questionId: z.uuid(),
  selectedOptionId: z.uuid().optional(),
  selectedOptionIds: z.array(z.uuid()).optional(),
  textAnswer: z.string().max(2000).optional(),
  timeSpentSeconds: z.number().int().min(0).optional(),
});

/**
 * Submit quiz schema
 */
export const submitQuizSchema = z.object({
  missionId: z.uuid(),
  journeyInstanceId: z.uuid(),
  answers: z.array(quizAnswerSchema).min(1),
  totalTimeSeconds: z.number().int().min(0),
});

// ═══════════════════════════════════════════════════════════════════════
// KNOWLEDGE ASSESSMENT SCHEMAS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Start knowledge assessment schema
 */
export const startKnowledgeAssessmentSchema = z.object({
  journeyInstanceId: z.uuid(),
  worldId: z.uuid().optional(), // Optional: specific world assessment
});

/**
 * Submit knowledge assessment schema
 */
export const submitKnowledgeAssessmentSchema = z.object({
  assessmentId: z.uuid(),
  journeyInstanceId: z.uuid(),
  answers: z.array(quizAnswerSchema).min(1),
  totalTimeSeconds: z.number().int().min(0),
});

// ═══════════════════════════════════════════════════════════════════════
// CONTENT SCHEMAS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Content interaction schema
 */
export const contentInteractionSchema = z.object({
  contentId: z.uuid(),
  contentType: z.enum(['video', 'article', 'infographic', 'quiz', 'interactive']),
  action: z.enum(['started', 'paused', 'resumed', 'completed', 'skipped']),
  progressPercentage: z.number().min(0).max(100).optional(),
  timeSpentSeconds: z.number().int().min(0).optional(),
});

/**
 * Bookmark content schema
 */
export const bookmarkContentSchema = z.object({
  contentId: z.uuid(),
  missionId: z.uuid(),
  note: z.string().max(500).optional(),
});

// ═══════════════════════════════════════════════════════════════════════
// TYPE EXPORTS
// ═══════════════════════════════════════════════════════════════════════

export type WorldProgress = z.infer<typeof worldProgressSchema>;
export type TrackProgress = z.infer<typeof trackProgressSchema>;
export type StartMissionInput = z.infer<typeof startMissionSchema>;
export type CompleteMissionInput = z.infer<typeof completeMissionSchema>;
export type MissionFeedbackInput = z.infer<typeof missionFeedbackSchema>;
export type QuizAnswer = z.infer<typeof quizAnswerSchema>;
export type SubmitQuizInput = z.infer<typeof submitQuizSchema>;
export type StartKnowledgeAssessmentInput = z.infer<typeof startKnowledgeAssessmentSchema>;
export type SubmitKnowledgeAssessmentInput = z.infer<typeof submitKnowledgeAssessmentSchema>;
export type ContentInteractionInput = z.infer<typeof contentInteractionSchema>;
export type BookmarkContentInput = z.infer<typeof bookmarkContentSchema>;
